import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Play, ChevronDown, ChevronUp, Info, Copy } from "lucide-react";
import { toast } from "sonner";

const API = '/api';

const SENDERS = [
  { email: "alice@acme.com", name: "Alice Novak" },
  { email: "bob@acme.com", name: "Bob Horvat" },
  { email: "clara@acme.com", name: "Clara Zupan" },
  { email: "david@acme.com", name: "David Krajnc" },
  { email: "eva@acme.eu", name: "Eva Potocnik" },
  { email: "frank@subsidiary.com", name: "Frank Kovac" },
];

const MODE_STYLES = {
  FULL: { bg: "bg-[#0F6CBD]", text: "text-white", label: "Full Signature" },
  SHORT: { bg: "bg-[#D83B01]", text: "text-white", label: "Short Signature" },
  NONE: { bg: "bg-[#F3F2F1]", text: "text-[#5E5E5E]", label: "No Signature" },
};

export default function TestProcessor() {
  const [form, setForm] = useState({
    from_address: "alice@acme.com",
    to: "client@external.com",
    cc: "",
    bcc: "",
    subject: "New Business Proposal",
    body: "<p>Dear Client,</p>\n<p>Please find attached our proposal for the upcoming project.</p>\n<p>Best regards</p>",
    body_type: "html",
  });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const processEmail = async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = {
        from_address: form.from_address,
        to: form.to.split(",").map((s) => s.trim()).filter(Boolean),
        cc: form.cc ? form.cc.split(",").map((s) => s.trim()).filter(Boolean) : [],
        bcc: form.bcc ? form.bcc.split(",").map((s) => s.trim()).filter(Boolean) : [],
        subject: form.subject,
        headers: {},
        body: form.body,
        body_type: form.body_type,
      };
      const resp = await axios.post(`${API}/processor/test`, payload);
      setResult(resp.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Processing failed");
    }
    setLoading(false);
  };

  const updateField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));
  const modeStyle = result ? MODE_STYLES[result.decision.mode] || MODE_STYLES.NONE : null;

  return (
    <div className="p-8 max-w-[1600px]" data-testid="test-processor-page">
      <h1 className="font-heading text-2xl font-semibold tracking-tight text-[#171717]">Test Processor</h1>
      <p className="text-sm text-[#5E5E5E] mt-1">
        Simulate email processing to preview signature decisions and rendering
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        {/* Left: Form */}
        <div className="bg-white border border-[#E1DFDD] rounded-sm" data-testid="email-form-panel">
          <div className="px-5 py-3 border-b border-[#E1DFDD]">
            <h2 className="font-heading text-base font-medium text-[#171717]">Compose Test Email</h2>
          </div>
          <div className="p-5 space-y-4">
            <div>
              <label className="text-xs font-semibold tracking-[0.05em] uppercase text-[#5E5E5E] mb-1.5 block">
                From
              </label>
              <select
                value={form.from_address}
                onChange={(e) => updateField("from_address", e.target.value)}
                data-testid="from-select"
                className="w-full bg-white border border-[#E1DFDD] rounded-sm px-3 py-2 text-sm text-[#171717] focus:outline-none focus:border-[#0F6CBD] focus:ring-1 focus:ring-[#0F6CBD]"
              >
                {SENDERS.map((s) => (
                  <option key={s.email} value={s.email}>
                    {s.name} &lt;{s.email}&gt;
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-xs font-semibold tracking-[0.05em] uppercase text-[#5E5E5E] mb-1.5 block">
                To (comma-separated)
              </label>
              <Input
                value={form.to}
                onChange={(e) => updateField("to", e.target.value)}
                placeholder="recipient@example.com"
                data-testid="to-input"
                className="border-[#E1DFDD] rounded-sm focus:border-[#0F6CBD] focus:ring-[#0F6CBD]"
              />
            </div>

            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="text-xs text-[#0F6CBD] flex items-center gap-1 hover:underline"
              data-testid="toggle-advanced-btn"
            >
              {showAdvanced ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
              {showAdvanced ? "Hide" : "Show"} CC / BCC
            </button>

            {showAdvanced && (
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-semibold tracking-[0.05em] uppercase text-[#5E5E5E] mb-1.5 block">CC</label>
                  <Input
                    value={form.cc}
                    onChange={(e) => updateField("cc", e.target.value)}
                    placeholder="cc@example.com"
                    data-testid="cc-input"
                    className="border-[#E1DFDD] rounded-sm"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold tracking-[0.05em] uppercase text-[#5E5E5E] mb-1.5 block">BCC</label>
                  <Input
                    value={form.bcc}
                    onChange={(e) => updateField("bcc", e.target.value)}
                    placeholder="bcc@example.com"
                    data-testid="bcc-input"
                    className="border-[#E1DFDD] rounded-sm"
                  />
                </div>
              </div>
            )}

            <div>
              <label className="text-xs font-semibold tracking-[0.05em] uppercase text-[#5E5E5E] mb-1.5 block">
                Subject
              </label>
              <Input
                value={form.subject}
                onChange={(e) => updateField("subject", e.target.value)}
                placeholder="Email subject"
                data-testid="subject-input"
                className="border-[#E1DFDD] rounded-sm"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-semibold tracking-[0.05em] uppercase text-[#5E5E5E]">Body</label>
                <div className="flex gap-1">
                  {["html", "text"].map((t) => (
                    <button
                      key={t}
                      onClick={() => updateField("body_type", t)}
                      data-testid={`body-type-${t}-btn`}
                      className={`px-2 py-0.5 text-xs rounded-sm transition-colors ${
                        form.body_type === t
                          ? "bg-[#0F6CBD] text-white"
                          : "bg-[#F3F2F1] text-[#5E5E5E] hover:bg-[#E1DFDD]"
                      }`}
                    >
                      {t.toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>
              <Textarea
                value={form.body}
                onChange={(e) => updateField("body", e.target.value)}
                rows={6}
                data-testid="body-textarea"
                className="border-[#E1DFDD] rounded-sm font-mono text-xs"
              />
            </div>

            {error && <p className="text-sm text-[#A4262C]" data-testid="error-msg">{error}</p>}

            <Button
              onClick={processEmail}
              disabled={loading || !form.to}
              data-testid="process-email-btn"
              className="w-full bg-[#0F6CBD] text-white hover:bg-[#005A9E] rounded-sm"
            >
              <Play size={14} className="mr-2" />
              {loading ? "Processing..." : "Process Email"}
            </Button>
          </div>
        </div>

        {/* Right: Results */}
        <div className="space-y-4" data-testid="results-panel">
          {!result && (
            <div className="bg-white border border-[#E1DFDD] rounded-sm p-8 text-center">
              <Info size={32} className="mx-auto text-[#E1DFDD]" />
              <p className="text-sm text-[#5E5E5E] mt-3">
                Compose and process a test email to see the signature decision and rendered output.
              </p>
            </div>
          )}

          {result && (
            <>
              {/* Effective Decision Panel */}
              {result.effective && (
                <div className="bg-[#171717] text-white rounded-sm" data-testid="effective-decision-panel">
                  <div className="px-5 py-3 border-b border-white/10">
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-white/70">Effective Decision</h3>
                  </div>
                  <div className="p-5 grid grid-cols-2 gap-x-6 gap-y-2 text-xs font-mono">
                    {Object.entries(result.effective).map(([key, val]) => (
                      <div key={key} className="flex justify-between py-0.5">
                        <span className="text-white/50">{key.replace(/_/g, " ")}</span>
                        <span className={`${val === "NONE" || val === "None" || val === null ? "text-white/30" : key === "signature_mode" ? "text-[#60CDFF] font-semibold" : "text-white"}`}>
                          {val === null ? "none" : val === true ? "Yes" : val === false ? "No" : String(val)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Decision Card */}
              <div className="bg-white border border-[#E1DFDD] rounded-sm" data-testid="decision-card">
                <div className="px-5 py-3 border-b border-[#E1DFDD] flex items-center justify-between">
                  <h3 className="font-heading text-sm font-medium text-[#171717]">Decision</h3>
                  <span className={`px-2.5 py-1 rounded-sm text-xs font-semibold ${modeStyle.bg} ${modeStyle.text}`}>
                    {result.decision.mode}
                  </span>
                </div>
                <div className="p-5 grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-[10px] uppercase tracking-wider text-[#5E5E5E] font-semibold">Reason</span>
                    <p className="text-[#171717] mt-0.5">{result.decision.reason.replace(/_/g, " ")}</p>
                  </div>
                  <div>
                    <span className="text-[10px] uppercase tracking-wider text-[#5E5E5E] font-semibold">Message Kind</span>
                    <p className="text-[#171717] mt-0.5">{result.decision.message_kind}</p>
                  </div>
                  <div>
                    <span className="text-[10px] uppercase tracking-wider text-[#5E5E5E] font-semibold">External</span>
                    <p className="text-[#171717] mt-0.5">{result.decision.is_external ? "Yes" : "No"}</p>
                  </div>
                  <div>
                    <span className="text-[10px] uppercase tracking-wider text-[#5E5E5E] font-semibold">Thread Key</span>
                    <p className="text-[#171717] mt-0.5 font-mono text-xs truncate">{result.decision.thread_key}</p>
                  </div>
                </div>
              </div>

              {/* Placement Info */}
              {result.placement && (
                <div className="bg-white border border-[#E1DFDD] rounded-sm" data-testid="placement-card">
                  <div className="px-5 py-3 border-b border-[#E1DFDD]">
                    <h3 className="font-heading text-sm font-medium text-[#171717]">Placement</h3>
                  </div>
                  <div className="p-5 grid grid-cols-3 gap-3 text-sm">
                    <div>
                      <span className="text-[10px] uppercase tracking-wider text-[#5E5E5E] font-semibold">Confidence</span>
                      <p className="text-[#171717] mt-0.5">
                        <span className={`inline-block w-2 h-2 rounded-full mr-1.5 ${
                          result.placement.confidence === "high" ? "bg-[#107C10]" :
                          result.placement.confidence === "medium" ? "bg-[#D83B01]" : "bg-[#A4262C]"
                        }`} />
                        {result.placement.confidence}
                      </p>
                    </div>
                    <div>
                      <span className="text-[10px] uppercase tracking-wider text-[#5E5E5E] font-semibold">New Content</span>
                      <p className="text-[#171717] mt-0.5">{result.placement.newest_length} chars</p>
                    </div>
                    <div>
                      <span className="text-[10px] uppercase tracking-wider text-[#5E5E5E] font-semibold">Quoted</span>
                      <p className="text-[#171717] mt-0.5">{result.placement.quoted_length} chars</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Signature Preview */}
              {result.signature_html && (
                <div className="bg-white border border-[#E1DFDD] rounded-sm" data-testid="signature-preview">
                  <div className="px-5 py-3 border-b border-[#E1DFDD] flex items-center justify-between">
                    <h3 className="font-heading text-sm font-medium text-[#171717]">Signature Preview</h3>
                    <div className="flex gap-1">
                      <button
                        onClick={() => { navigator.clipboard.writeText(result.signature_html); toast.success("HTML copied to clipboard"); }}
                        className="flex items-center gap-1 px-2 py-1 text-[10px] text-[#0F6CBD] hover:bg-[#EBF3FC] rounded-sm transition-colors"
                        data-testid="copy-sig-html-btn"
                      >
                        <Copy size={11} /> Copy HTML
                      </button>
                      {result.signature_text && (
                        <button
                          onClick={() => { navigator.clipboard.writeText(result.signature_text); toast.success("Plain text copied"); }}
                          className="flex items-center gap-1 px-2 py-1 text-[10px] text-[#5E5E5E] hover:bg-[#F3F2F1] rounded-sm transition-colors"
                          data-testid="copy-sig-text-btn"
                        >
                          <Copy size={11} /> Copy Text
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="p-5">
                    <div
                      className="border border-[#E1DFDD] rounded-sm p-4 bg-[#FAFAFA]"
                      dangerouslySetInnerHTML={{ __html: result.signature_html }}
                    />
                  </div>
                </div>
              )}

              {/* Resolution Sources */}
              {result.render_context?.resolution_sources && (
                <div className="bg-white border border-[#E1DFDD] rounded-sm" data-testid="resolution-sources">
                  <div className="px-5 py-3 border-b border-[#E1DFDD]">
                    <h3 className="font-heading text-sm font-medium text-[#171717]">Data Resolution Sources</h3>
                  </div>
                  <div className="p-5">
                    <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
                      {Object.entries(result.render_context.resolution_sources).map(([field, source]) => (
                        <div key={field} className="flex justify-between py-1 border-b border-[#F3F2F1]">
                          <span className="text-[#5E5E5E]">{field.replace(/_/g, " ")}</span>
                          <span className={`font-mono ${source === "graph" ? "text-[#0F6CBD]" : source === "blank" ? "text-[#A4262C]" : "text-[#107C10]"}`}>
                            {source}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
